import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import { Settings, Download, Plus, X, Maximize2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.jsx";
import { Label } from "@/components/ui/label.jsx";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.jsx";

const defaultFields = [
  {
    id: "ca",
    name: "Chiffres d'affaire",
    values: { 2022: "84667 €", 2021: "—" },
  },
  {
    id: "cp",
    name: "Capitaux propres",
    values: { 2022: "26338 €", 2021: "—" },
  },
  {
    id: "dispo",
    name: "Disponibilité",
    values: { 2022: "54826 €", 2021: "—" },
  },
  {
    id: "re",
    name: "Résultat d'exploitation",
    values: { 2022: "49873 €", 2021: "—" },
  },
  {
    id: "rn",
    name: "Résultat net",
    values: { 2022: "28 450,75 €", 2021: "—" },
  },
  {
    id: "td",
    name: "Total dettes",
    values: { 2022: "30220 €", 2021: "—" },
  },
  {
    id: "pi",
    name: "Production immobilisée",
    values: { 2022: "104933 €", 2021: "—" },
  },
  {
    id: "ran",
    name: "Report à nouveau",
    values: { 2022: "44696 €", 2021: "—" },
  },
];

const defaultRatios = [
  {
    id: "1",
    name: "Résultat d'exploitation / CA",
    tokens: [
      { type: "field", value: "re" },
      { type: "op", value: "/" },
      { type: "field", value: "ca" },
    ],
    unit: "percentage",
    value: "12%",
  },
  {
    id: "2",
    name: "Capitaux propres / Dettes",
    tokens: [
      { type: "field", value: "cp" },
      { type: "op", value: "/" },
      { type: "field", value: "td" },
    ],
    unit: "percentage",
    value: "25%",
  },
];

export function FiscalDataManager() {
  const [fields, setFields] = useState(defaultFields);
  const [ratios, setRatios] = useState(defaultRatios);
  const [newFieldName, setNewFieldName] = useState("");
  const [newRatioName, setNewRatioName] = useState("");
  const [showCustomization, setShowCustomization] = useState(false);

  // Builder state for custom ratios
  const [newRatioTokens, setNewRatioTokens] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [selectedOperator, setSelectedOperator] = useState("/");
  const [constNumber, setConstNumber] = useState("");
  const [newRatioUnit, setNewRatioUnit] = useState("percentage");

  const removeField = (fieldId) => {
    setFields(fields.filter((f) => f.id !== fieldId));
  };

  const addField = () => {
    if (newFieldName.trim()) {
      const newField = {
        id: Date.now().toString(),
        name: newFieldName.trim(),
        values: { 2022: "—", 2021: "—" },
      };
      setFields([...fields, newField]);
      setNewFieldName("");
    }
  };

  // Helpers to compute ratios
  const parseToNumber = (val) => {
    if (!val) return 0;
    const cleaned = val
      .toString()
      .replace(/[^0-9,.-]/g, "")
      .replace(/\s/g, "")
      .replace(/,(?=\d{1,2}$)/, ".")
      .replace(/,/g, "");
    const num = Number(cleaned);
    return isNaN(num) ? 0 : num;
  };

  const getFieldValue = (fieldId, year) => {
    const f = fields.find((fl) => fl.id === fieldId);
    return parseToNumber(f?.values[year]);
  };

  const tokensToExpression = (tokens, year) => {
    return tokens
      .map((t) => {
        if (t.type === "op") return t.value;
        if (t.type === "number") return String(parseToNumber(t.value));
        // field
        return String(getFieldValue(t.value, year));
      })
      .join(" ");
  };

  const safeEvaluate = (expr) => {
    // Only allow numbers, operators and spacing
    if (!/^[-+*/(). 0-9]+$/.test(expr)) return 0;
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function(`return (${expr})`);
      const res = fn();
      return typeof res === "number" && isFinite(res) ? res : 0;
    } catch {
      return 0;
    }
  };

  const formatValue = (num, unit) => {
    if (unit === "percentage") {
      return `${(num * 100).toFixed(2)}%`;
    }
    return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(
      num,
    );
  };

  const addOperand = () => {
    if (selectedFieldId) {
      setNewRatioTokens((prev) => [
        ...prev,
        { type: "field", value: selectedFieldId },
      ]);
      setSelectedFieldId("");
    } else if (constNumber.trim() !== "") {
      setNewRatioTokens((prev) => [
        ...prev,
        { type: "number", value: constNumber.trim() },
      ]);
      setConstNumber("");
    }
  };

  const addOp = () => {
    if (
      newRatioTokens.length &&
      newRatioTokens[newRatioTokens.length - 1].type !== "op"
    ) {
      setNewRatioTokens((prev) => [
        ...prev,
        { type: "op", value: selectedOperator },
      ]);
    }
  };

  const resetBuilder = () => setNewRatioTokens([]);

  const addRatio = () => {
    const last = newRatioTokens[newRatioTokens.length - 1];
    if (
      !newRatioName.trim() ||
      newRatioTokens.length < 3 ||
      !last ||
      last.type === "op"
    )
      return;

    const expr2022 = tokensToExpression(newRatioTokens, "2022");
    const result = safeEvaluate(expr2022);
    const formatted = formatValue(result, newRatioUnit);

    const newRatio = {
      id: Date.now().toString(),
      name: newRatioName.trim(),
      tokens: newRatioTokens,
      unit: newRatioUnit,
      value: formatted,
    };
    setRatios([...ratios, newRatio]);
    setNewRatioName("");
    setNewRatioTokens([]);
    setNewRatioUnit("percentage");
  };

  const downloadCSV = () => {
    const headers = ["Champ", "2022", "2021"];
    const rows = fields.map((field) => [
      field.name,
      field.values["2022"] || "—",
      field.values["2021"] || "—",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "donnees_fiscales.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Popup pour personnaliser les données fiscales */}
      <Dialog>
        <DialogTrigger asChild>
          <Card className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Personnaliser les données fiscales à extraire
              </CardTitle>
            </CardHeader>
          </Card>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Personnaliser les données fiscales à extraire
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-3">Champs actuels</h3>
              <div className="flex flex-wrap gap-2">
                {fields.map((field) => (
                  <Badge
                    key={field.id}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {field.name}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 w-4 h-4"
                      onClick={() => removeField(field.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-medium mb-3">Ajouter un nouveau champ</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="Nom du champ"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={addField}
                  className="bg-primary text-primary-foreground"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-medium mb-3">
                Ajouter un ratio personnalisé
              </h3>
              <div className="space-y-3">
                <Input
                  placeholder="Nom du ratio"
                  value={newRatioName}
                  onChange={(e) => setNewRatioName(e.target.value)}
                />

                {/* Bloc "Construire l'expression" supprimé selon la demande */}

                <div className="space-y-2">
                  <Label>Unité</Label>
                  <RadioGroup
                    value={newRatioUnit}
                    onValueChange={(v) => setNewRatioUnit(v)}
                    className="flex gap-6"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="absolute" id="unit-abs" />
                      <Label htmlFor="unit-abs">Valeur absolue</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="percentage" id="unit-pct" />
                      <Label htmlFor="unit-pct">Pourcentage</Label>
                    </div>
                  </RadioGroup>
                </div>

                <Button
                  onClick={addRatio}
                  className="w-full bg-primary text-primary-foreground"
                  disabled={
                    !newRatioName.trim() ||
                    newRatioTokens.length < 3 ||
                    newRatioTokens[newRatioTokens.length - 1]?.type === "op"
                  }
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter ce ratio
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {ratios.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-4">Ratios personnalisés</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ratios.map((ratio) => (
              <div key={ratio.id} className="space-y-1">
                <p className="text-sm text-muted-foreground">{ratio.name}</p>
                <p className="text-2xl font-bold">{ratio.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tableau des données fiscales avec bouton agrandir */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Données fiscales</CardTitle>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Maximize2 className="h-4 w-4 mr-2" />
                Agrandir
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Données fiscales - Vue agrandie</DialogTitle>
              </DialogHeader>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-medium text-lg">
                        Champ
                      </TableHead>
                      <TableHead className="text-right font-medium text-lg">
                        2022
                      </TableHead>
                      <TableHead className="text-right font-medium text-lg">
                        2021
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fields.map((field) => (
                      <TableRow key={field.id}>
                        <TableCell className="font-medium text-base">
                          {field.name}
                        </TableCell>
                        <TableCell className="text-right text-base">
                          {field.values["2022"]}
                        </TableCell>
                        <TableCell className="text-right text-base">
                          {field.values["2021"]}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-medium">Champ</TableHead>
                  <TableHead className="text-right font-medium">2022</TableHead>
                  <TableHead className="text-right font-medium">2021</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fields.map((field) => (
                  <TableRow key={field.id}>
                    <TableCell className="font-medium">{field.name}</TableCell>
                    <TableCell className="text-right">
                      {field.values["2022"]}
                    </TableCell>
                    <TableCell className="text-right">
                      {field.values["2021"]}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button variant="outline" onClick={downloadCSV}>
          <Download className="h-4 w-4 mr-2" />
          Télécharger CSV
        </Button>
      </div>
    </div>
  );
}
